import React, { useMemo } from 'react';
import ManCrossPageFooter from '../components/ManCrossPageFooter';
import {
    computeIvcLevel,
    formatRounded,
    toDimensionRows,
} from '../shared/managerialUtils';

// ── Card color config (matches semaphore palette) ─────────────────────────────
const CARD_COLORS = {
    red:    { circle: '#F3202A', title: '#F3202A', accent: '#F3202A', accentBg: '#FDE6E8' },
    orange: { circle: '#F48732', title: '#F48732', accent: '#F48732', accentBg: '#FEF0E6' },
    green:  { circle: '#62C190', title: '#62C190', accent: '#62C190', accentBg: '#E7F6EC' },
};

// ── Narrative builders ────────────────────────────────────────────────────────

function buildCard1(areas) {
    const risky = areas
        .filter(a => a.ivcLevel > 0)
        .sort((a, b) => b.ivcLevel - a.ivcLevel || (a.icp ?? 999) - (b.icp ?? 999));

    if (risky.length === 0) {
        return 'No se identificaron áreas en condición de riesgo IVC en esta medición. Mantener monitoreo preventivo.';
    }

    const top    = risky[0];
    const level  = top.ivcLevel;
    const cofail = level >= 2 ? 'Co-falla activa IPRA + IASH' : 'Falla parcial en IPRA o IASH';

    if (risky.length >= 2) {
        const second = risky[1];
        return `${top.name} y ${second.name}. ${cofail} en ${top.name} = IVC Nivel ${level} — la condición de mayor emergencia del sistema.`;
    }
    return `${top.name}. ${cofail} = IVC Nivel ${level} — la condición de mayor emergencia del sistema.`;
}

function buildCard2(criticalSups, iashTotals, headcountMap) {
    if (!criticalSups || criticalSups.length === 0) {
        return 'No se encontraron supervisores con IPRA por debajo del umbral de intervención en esta medición.';
    }

    const top    = criticalSups[0];
    const n      = criticalSups.length;
    const area   = top.management_area || 'el área';
    const score  = top.ipra_score != null ? Math.round(top.ipra_score) : '–';
    const dim    = top.worst_dimension?.title || 'la dimensión crítica';
    const wPct   = top.worst_dimension?.weight_percent != null
        ? `${Math.round(top.worst_dimension.weight_percent)}%`
        : null;

    // Deficit share: (IASH deficit for top area) / (total plant IASH deficit)
    const IASH_TARGET = 80;
    let totalDeficit = 0;
    let areaDeficit  = 0;
    (iashTotals?.average_by_management_area || []).forEach(a => {
        const hc = headcountMap[a.name] ?? a.count ?? 1;
        const d  = Math.max(0, IASH_TARGET - (a.average ?? IASH_TARGET)) * hc;
        totalDeficit += d;
        if (a.name === top.management_area) areaDeficit += d;
    });
    const deficitPct = totalDeficit > 0 ? Math.round(areaDeficit / totalDeficit * 100) : null;

    const parts = [
        `${n} supervisor${n > 1 ? 'es' : ''} de ${area} ${n > 1 ? 'tienen' : 'tiene'} IPRA < ${score} en ${dim}${wPct ? ` — la barrera de mayor peso (${wPct})` : ''}.`,
    ];
    if (deficitPct != null) {
        parts.push(`Sus operarios concentran el ${deficitPct}% del déficit de autocuidado de toda la planta.`);
    }
    return parts.join(' ');
}

function buildCard3(icp, areas) {
    const icpVal = typeof icp === 'number' ? Math.round(icp) : null;
    if (icpVal === null) return 'No hay datos suficientes para estimar el impacto proyectado.';

    const TARGET        = 85;
    const yearsToTarget = Math.max(1, Math.ceil((TARGET - icpVal) / 5));

    const topRisk = areas
        .filter(a => a.ivcLevel >= 2)
        .sort((a, b) => (a.icp ?? 999) - (b.icp ?? 999))[0]
        ?? areas
            .filter(a => a.ivcLevel >= 1)
            .sort((a, b) => (a.icp ?? 999) - (b.icp ?? 999))[0];

    const parts = [
        `Con ICP=${icpVal} y tendencia estancada, la planta tardará ${yearsToTarget}+ años en llegar a zona segura.`,
    ];
    if (topRisk) {
        const areaIcp = topRisk.icp ?? icpVal;
        const mult    = Math.max(1.5, ((100 - areaIcp) / Math.max(1, 100 - icpVal)));
        parts.push(
            `La probabilidad de incidente grave en ${topRisk.name} es ${Math.round(mult * 10) / 10}× mayor que en áreas sin co-falla.`
        );
    }
    return parts.join(' ');
}

function buildCard4(criticalSups, ipraBottomDim, iashBottomDim) {
    const parts = [];

    if (criticalSups?.length >= 2) {
        parts.push(`Intervención directa ${criticalSups[0].code} y ${criticalSups[1].code} — Jefe SST esta semana.`);
    } else if (criticalSups?.length === 1) {
        parts.push(`Intervención directa ${criticalSups[0].code} — Jefe SST esta semana.`);
    }

    if (ipraBottomDim?.dimension) {
        parts.push(`Taller ${ipraBottomDim.dimension} para supervisores — SST.`);
    }
    if (iashBottomDim?.dimension) {
        parts.push(`Programa ${iashBottomDim.dimension} — Gestión Humana.`);
    }

    return parts.join(' ') || 'Continuar monitoreo de todos los indicadores.';
}

// ── DiagnosisCard ─────────────────────────────────────────────────────────────
const DiagnosisCard = ({ number, question, body, colorKey }) => {
    const c = CARD_COLORS[colorKey] ?? CARD_COLORS.red;
    return (
        <div className="mgcr-dx-card">
            <div className="mgcr-dx-card-header">
                <span className="mgcr-dx-dot" style={{ background: c.circle }} />
                <span className="mgcr-dx-card-title" style={{ color: c.title }}>
                    {number}. {question}
                </span>
            </div>
            <div className="mgcr-dx-card-body" style={{ borderLeftColor: c.accent }}>
                {body}
            </div>
        </div>
    );
};

// ── Main component ────────────────────────────────────────────────────────────
const ManCrossPageDiagnosis = ({ reportData, headerText, pageNumber }) => {
    const payload     = reportData?.payload || {};
    const kpis        = payload?.kpis       || {};
    const iashTotals  = payload?.totals?.iash || {};
    const ipraTotals  = payload?.totals?.ipra || {};
    const criticalSups = payload?.critical_supervisors || [];

    // Headcount map
    const headcountMap = useMemo(() => {
        const map = {};
        (payload?.headcount_by_management_area || []).forEach(e => { map[e.name] = e.headcount; });
        return map;
    }, [payload]);

    // Per-area IVC data (same logic as RiskMap page)
    const areas = useMemo(() => {
        const iashMap = {};
        (iashTotals?.average_by_management_area || []).forEach(a => { iashMap[a.name] = a.average; });
        const ipraMap = {};
        (ipraTotals?.average_by_management_area || []).forEach(a => { ipraMap[a.name] = a.average; });

        const names = new Set([...Object.keys(iashMap), ...Object.keys(ipraMap)]);
        return [...names].map(name => {
            const iash    = iashMap[name] ?? null;
            const ipra    = ipraMap[name] ?? null;
            const icpRaw  = iash != null && ipra != null ? (iash + ipra) / 2 : (iash ?? ipra);
            const icp     = icpRaw != null ? Math.round(icpRaw) : null;
            return { name, iash, ipra, icp, ivcLevel: computeIvcLevel(ipra, iash) };
        });
    }, [iashTotals, ipraTotals]);

    // Worst IPRA and IASH dimensions for Card 4
    const ipraBottomDim = useMemo(() => {
        const rows = toDimensionRows(ipraTotals?.average_by_dimension)
            .filter(r => r.average != null);
        return rows.sort((a, b) => a.average - b.average)[0] ?? null;
    }, [ipraTotals]);

    const iashBottomDim = useMemo(() => {
        const rows = toDimensionRows(iashTotals?.average_by_dimension)
            .filter(r => r.average != null);
        return rows.sort((a, b) => a.average - b.average)[0] ?? null;
    }, [iashTotals]);

    // Generate narratives
    const card1Body = useMemo(() => buildCard1(areas), [areas]);
    const card2Body = useMemo(() => buildCard2(criticalSups, iashTotals, headcountMap), [criticalSups, iashTotals, headcountMap]);
    const card3Body = useMemo(() => buildCard3(kpis?.cross_index, areas), [kpis, areas]);
    const card4Body = useMemo(() => buildCard4(criticalSups, ipraBottomDim, iashBottomDim), [criticalSups, ipraBottomDim, iashBottomDim]);

    return (
        <div className="mgcr-dx-page">

            {/* ── Page header ── */}
            <div className="mgcr-page-header">
                <span className="mgcr-page-header-text">{headerText}</span>
                <span className="mgcr-page-header-num">{pageNumber}</span>
            </div>

            {/* ── Titles ── */}
            <div className="mgcr-dx-title-area">
                <h2 className="mgcr-dx-main-title">Diagnóstico Actual</h2>
                <p  className="mgcr-dx-subtitle">Prioridades de Gestión</p>
            </div>

            {/* ── 2×2 grid ── */}
            <div className="mgcr-dx-grid">
                <DiagnosisCard number={1} question="¿Dónde está el riesgo?"    body={card1Body} colorKey="red"    />
                <DiagnosisCard number={2} question="¿Por qué?"                 body={card2Body} colorKey="orange" />
                <DiagnosisCard number={3} question="¿Qué pasa si no actúo?"    body={card3Body} colorKey="red"    />
                <DiagnosisCard number={4} question="¿Qué hago esta semana?"    body={card4Body} colorKey="green"  />
            </div>

            {/* ── Disclaimer bar ── */}
            <div className="mgcr-dx-disclaimer">
                <span className="mgcr-dx-disclaimer-icon">⚠</span>
                <span className="mgcr-dx-disclaimer-text">
                    Uso exclusivo de gestión preventiva: La identificación de áreas o personas en zona de riesgo no constituye evidencia de responsabilidad individual ni obliga a actuar sobre trabajadores específicos. El criterio y la responsabilidad de las decisiones son exclusivos de la organización.
                </span>
            </div>

            <ManCrossPageFooter />
        </div>
    );
};

export default ManCrossPageDiagnosis;
